import { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

const CatalogNativeSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Seleziona...",
  className = "",
  disabled = false,
  id,
  ariaLabel,
  menuClassName = "",
  title,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();
  const triggerId = id || `cm-select-${listId}`;

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (nextValue, isDisabled) => {
    if (disabled || isDisabled) {
      return;
    }
    onChange?.(nextValue);
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`cm-native-select${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`.trim()}
    >
      <button
        type="button"
        id={triggerId}
        className="cm-native-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        title={title || ariaLabel || undefined}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((previous) => !previous);
          }
        }}
      >
        <span className="cm-native-select-value">
          {selectedOption?.label ?? placeholder}
        </span>
        <span className="cm-native-select-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={triggerId}
          className={`cm-native-select-menu${menuClassName ? ` ${menuClassName}` : ""}`.trim()}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            const isDisabledOption = Boolean(option.disabled);
            return (
              <li
                key={`${option.value}-${option.label}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabledOption}
                className={[
                  "cm-native-select-option",
                  isSelected ? "is-selected" : "",
                  isDisabledOption ? "is-disabled" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => handleSelect(option.value, isDisabledOption)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSelect(option.value, isDisabledOption);
                  }
                }}
                tabIndex={isDisabledOption ? -1 : 0}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

CatalogNativeSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
  })),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  menuClassName: PropTypes.string,
  title: PropTypes.string,
};

export default CatalogNativeSelect;
